import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuestionReview = ({ 
  questions = [
    {
      id: 1,
      question: "What is the primary purpose of machine learning?",
      options: [
        "To replace human intelligence completely",
        "To enable computers to learn and improve from experience",
        "To create robots that look like humans",
        "To store large amounts of data"
      ],
      userAnswer: 1,
      correctAnswer: 1,
      explanation: "Machine learning enables computers to learn patterns from data and improve their performance on tasks without being explicitly programmed for every scenario."
    },
    {
      id: 2,
      question: "Which of the following is NOT a type of machine learning?",
      options: [
        "Supervised Learning",
        "Unsupervised Learning",
        "Reinforcement Learning",
        "Quantum Learning"
      ],
      userAnswer: 2,
      correctAnswer: 3,
      explanation: "Quantum Learning is not a recognized type of machine learning. The three main types are Supervised, Unsupervised, and Reinforcement Learning."
    },
    {
      id: 3,
      question: "What does AI stand for?",
      options: [
        "Automated Intelligence",
        "Artificial Intelligence",
        "Advanced Integration",
        "Algorithmic Implementation"
      ],
      userAnswer: 1,
      correctAnswer: 1,
      explanation: "AI stands for Artificial Intelligence, which refers to the simulation of human intelligence in machines."
    },
    {
      id: 4,
      question: "Which company developed ChatGPT?",
      options: [
        "Google",
        "Microsoft",
        "OpenAI",
        "Meta"
      ],
      userAnswer: 2,
      correctAnswer: 2,
      explanation: "ChatGPT was developed by OpenAI, an AI research company founded in 2015."
    },
    {
      id: 5,
      question: "What is a neural network inspired by?",
      options: [
        "Computer circuits",
        "The human brain",
        "Mathematical equations",
        "Database structures"
      ],
      userAnswer: 1,
      correctAnswer: 1,
      explanation: "Neural networks are inspired by the structure and function of biological neural networks in the human brain."
    }
  ]
}) => {
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());

  const toggleExpanded = (questionId) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(questionId)) {
      newExpanded.delete(questionId);
    } else {
      newExpanded.add(questionId);
    }
    setExpandedQuestions(newExpanded);
  };

  const isCorrect = (question) => question.userAnswer === question.correctAnswer;

  const getQuestionIcon = (question) => {
    return isCorrect(question) ? "CheckCircle2" : "XCircle";
  };

  const getQuestionColor = (question) => {
    return isCorrect(question) ? "success" : "error";
  };

  const correctCount = questions.filter(q => isCorrect(q)).length;
  const incorrectCount = questions.length - correctCount;

  return (
    <div className="space-y-6">
      {/* Review Header */}
      <div className="bg-surface rounded-xl border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-heading font-bold text-text-primary">
            Question Review
          </h2>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-success rounded-full"></div>
              <span className="text-sm text-text-secondary">{correctCount} Correct</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-error rounded-full"></div>
              <span className="text-sm text-text-secondary">{incorrectCount} Incorrect</span>
            </div>
          </div>
        </div>
        <p className="text-text-secondary">
          Review your answers and learn from detailed explanations for each question.
        </p>
      </div>

      {/* Questions List */}
      <div className="space-y-4">
        {questions.map((question, index) => (
          <div 
            key={question.id}
            className={`
              bg-surface rounded-xl border transition-all duration-200
              ${isCorrect(question) 
                ? 'border-success-200 bg-success-50/30' :'border-error-200 bg-error-50/30'}
            `}
          >
            {/* Question Header */}
            <div className="p-6">
              <div className="flex items-start space-x-4">
                <div className={`
                  flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0
                  ${isCorrect(question) ? 'bg-success-100' : 'bg-error-100'}
                `}>
                  <Icon 
                    name={getQuestionIcon(question)} 
                    size={20} 
                    color={`var(--color-${getQuestionColor(question)})`}
                    strokeWidth={2}
                  />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-heading font-semibold text-text-primary">
                      Question {index + 1}
                    </h3>
                    <div className={`
                      px-3 py-1 rounded-full text-sm font-medium
                      ${isCorrect(question) 
                        ? 'bg-success-100 text-success-800' :'bg-error-100 text-error-800'}
                    `}>
                      {isCorrect(question) ? 'Correct' : 'Incorrect'}
                    </div>
                  </div>
                  
                  <p className="text-text-primary font-medium mb-4">
                    {question.question}
                  </p>

                  {/* Answer Options */}
                  <div className="space-y-2 mb-4">
                    {question.options.map((option, optionIndex) => {
                      const isUserAnswer = optionIndex === question.userAnswer;
                      const isCorrectAnswer = optionIndex === question.correctAnswer;
                      
                      let optionClass = "flex items-center space-x-3 p-3 rounded-lg border ";
                      
                      if (isCorrectAnswer) {
                        optionClass += "border-success-200 bg-success-50 text-success-800";
                      } else if (isUserAnswer && !isCorrectAnswer) {
                        optionClass += "border-error-200 bg-error-50 text-error-800";
                      } else {
                        optionClass += "border-border bg-surface-secondary text-text-secondary";
                      }

                      return (
                        <div key={optionIndex} className={optionClass}>
                          <div className="flex items-center space-x-2">
                            {isCorrectAnswer && (
                              <Icon name="CheckCircle2" size={16} color="var(--color-success)" strokeWidth={2} />
                            )}
                            {isUserAnswer && !isCorrectAnswer && (
                              <Icon name="XCircle" size={16} color="var(--color-error)" strokeWidth={2} />
                            )}
                            <span className="w-6 h-6 rounded-full border-2 border-current flex items-center justify-center text-xs font-medium">
                              {String.fromCharCode(65 + optionIndex)}
                            </span>
                          </div>
                          <span className="font-medium">{option}</span>
                          {isUserAnswer && (
                            <span className="ml-auto text-xs font-medium">Your Answer</span>
                          )}
                          {isCorrectAnswer && (
                            <span className="ml-auto text-xs font-medium">Correct Answer</span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Explanation Toggle */}
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName={expandedQuestions.has(question.id) ? "ChevronUp" : "ChevronDown"}
                    iconPosition="right"
                    onClick={() => toggleExpanded(question.id)}
                    className="text-text-secondary hover:text-text-primary"
                  >
                    {expandedQuestions.has(question.id) ? 'Hide' : 'Show'} Explanation
                  </Button>
                </div>
              </div>
            </div>

            {/* Explanation */}
            {expandedQuestions.has(question.id) && (
              <div className="px-6 pb-6">
                <div className="bg-surface rounded-lg border border-border p-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-primary-50 rounded-lg flex-shrink-0">
                      <Icon name="Lightbulb" size={16} color="var(--color-primary)" strokeWidth={2} />
                    </div>
                    <div>
                      <h4 className="font-heading font-semibold text-text-primary mb-2">
                        Explanation
                      </h4>
                      <p className="text-text-secondary leading-relaxed">
                        {question.explanation}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default QuestionReview;